import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import CircularProgress from '@material-ui/core/CircularProgress'
import Button from '../../../../../components/button'
import config from '../../../../../config'
import { exerciseLogin, exerciseLogout } from '../../../../../api'

const FrameContainer = styled.div`
  min-height: calc(100vh - 156px);
  padding: 16px 32px 32px;

  @media(max-width: 900px) {
    min-height: calc(100vh - 91px);
    padding: 44px 0 0;
  }
`

const ExerciseFrame = styled.iframe`
  border: 0;
  display: ${(props) => props.$visible ? 'block' : 'none'};
  height: calc(100vh - 188px);
  min-height: 640px;
  width: 100%;

  @media(max-width: 900px) {
    height: calc(100vh - 91px);
    min-height: 560px;
  }
`

const Status = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 20px;
  justify-content: center;
  min-height: 420px;
  padding: 32px;
  text-align: center;
`

const MESSAGE = {
  init: 'AVN_EXERCISE_EMBED_INIT',
  ready: 'AVN_EXERCISE_EMBED_READY',
  auth: 'AVN_EXERCISE_EMBED_AUTH',
  authenticated: 'AVN_EXERCISE_EMBED_AUTHENTICATED',
  error: 'AVN_EXERCISE_EMBED_ERROR',
  logout: 'AVN_EXERCISE_EMBED_LOGOUT',
  loggedOut: 'AVN_EXERCISE_EMBED_LOGGED_OUT',
}

export default function Exercise({ managerId, accountId }) {
  const iframeRef = useRef(null)
  const tokensRef = useRef(null)
  const logoutStartedRef = useRef(false)
  const iframeReadyRef = useRef(false)
  const iframeLoadedRef = useRef(false)
  const authSentRef = useRef(false)
  const [attempt, setAttempt] = useState(0)
  const [status, setStatus] = useState('loading')
  const [error, setError] = useState('')

  const sendInitialization = useCallback(() => {
    const frameWindow = iframeRef.current && iframeRef.current.contentWindow
    if (!iframeLoadedRef.current || !frameWindow || iframeReadyRef.current) return

    frameWindow.postMessage({ type: MESSAGE.init }, config.EXERCISE_ROOT)
  }, [accountId])

  const sendAuthentication = useCallback(() => {
    const frameWindow = iframeRef.current && iframeRef.current.contentWindow
    const tokens = tokensRef.current
    if (!iframeReadyRef.current || !frameWindow || !tokens || authSentRef.current) return

    authSentRef.current = true
    frameWindow.postMessage({ type: MESSAGE.auth, ...tokens }, config.EXERCISE_ROOT)
    setStatus('authenticating')
  }, [accountId])

  const revokeSession = useCallback(() => {
    if (logoutStartedRef.current) return
    logoutStartedRef.current = true

    const tokens = tokensRef.current
    tokensRef.current = null
    if (!tokens) return

    if (iframeRef.current && iframeRef.current.contentWindow) {
      iframeRef.current.contentWindow.postMessage({ type: MESSAGE.logout }, config.EXERCISE_ROOT)
    }

    exerciseLogout(managerId, accountId, tokens.refreshToken).catch(() => {})
  }, [accountId, managerId])

  useEffect(() => {
    let cancelled = false
    logoutStartedRef.current = false
    authSentRef.current = false
    iframeReadyRef.current = false
    iframeLoadedRef.current = false
    setStatus('loading')
    setError('')

    exerciseLogin(managerId, accountId)
      .then((response) => {
        if (cancelled) {
          exerciseLogout(managerId, accountId, response.data.refresh_token).catch(() => {})
          return
        }
        tokensRef.current = {
          accessToken: response.data.access_token,
          refreshToken: response.data.refresh_token,
        }
        setStatus('waiting')
        sendAuthentication()
      })
      .catch(() => {
        if (!cancelled) {
          setError('Unable to start the Exercise session. Please try again.')
          setStatus('error')
        }
      })

    return () => {
      cancelled = true
      revokeSession()
    }
  }, [accountId, attempt, managerId, revokeSession, sendAuthentication])

  useEffect(() => {
    if (status === 'authenticated' || status === 'error') return

    sendInitialization()
    const interval = window.setInterval(sendInitialization, 1000)
    return () => window.clearInterval(interval)
  }, [sendInitialization, status])

  useEffect(() => {
    const handleMessage = (event) => {
      const sourceMatches = Boolean(iframeRef.current && event.source === iframeRef.current.contentWindow)
      const type = event.data && typeof event.data.type === 'string' ? event.data.type : 'invalid'
      if (event.origin !== config.EXERCISE_ROOT) return
      if (!sourceMatches) return
      if (type === 'invalid') return

      if (type === MESSAGE.ready) {
        iframeReadyRef.current = true
        sendAuthentication()
      } else if (type === MESSAGE.authenticated) {
        setStatus('authenticated')
      } else if (type === MESSAGE.error) {
        setError(event.data.message || 'Exercise could not authenticate this session.')
        setStatus('error')
      } else if (type === MESSAGE.loggedOut) {
        tokensRef.current = null
        setError('The Exercise session has ended.')
        setStatus('error')
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [sendAuthentication])

  const retry = () => {
    revokeSession()
    logoutStartedRef.current = false
    setAttempt((value) => value + 1)
  }

  return (
    <FrameContainer>
      {status !== 'error' && status !== 'authenticated' && (
        <Status>
          <CircularProgress size={56} />
          <p>{status === 'loading' ? 'Starting Exercise session...' : 'Opening Exercise dashboard...'}</p>
        </Status>
      )}
      {status === 'error' && (
        <Status role="alert">
          <p>{error}</p>
          <Button label="Try again" onClick={retry} />
        </Status>
      )}
      <ExerciseFrame
        key={`${accountId}-${attempt}`}
        ref={iframeRef}
        src={`${config.EXERCISE_ROOT}/portal-embed`}
        title="Avatar Exercise"
        $visible={status === 'authenticated'}
        aria-hidden={status !== 'authenticated'}
        allow="fullscreen"
        onLoad={() => {
          iframeLoadedRef.current = true
          sendInitialization()
        }}
      />
    </FrameContainer>
  )
}
